import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, US_STATES, STATE_SLUG_TO_CODE, STATE_CODE_TO_NAME, stateSlug, careTypeLabel, facilityTypeLabel, specialtyLabel, fileUrl } from "@/lib/api";
import { getStateSEOContent } from "@/data/stateSeo";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MapPin, Phone, ArrowRight, Building2, Search as SearchIcon } from "lucide-react";

const HERO_IMG =
  "https://images.pexels.com/photos/3768131/pexels-photo-3768131.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";

export default function StateLanding() {
  const { stateSlug: slug } = useParams();
  const navigate = useNavigate();
  const stateCode = STATE_SLUG_TO_CODE[slug];
  const stateName = stateCode ? STATE_CODE_TO_NAME[stateCode] : null;
  const seo = stateCode ? getStateSEOContent(stateCode) : null;
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!stateCode) return;
    document.title = `Adult Day Care, Memory Care, Respite & Hospice in ${stateName} — Providence Partners`;
    // Update or create meta description for SEO
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    const desc = seo
      ? `Browse vetted adult day care, memory care, and respite homes in ${stateName}. ${seo.intro.substring(0, 120)}`
      : `Browse vetted adult day care, memory care, and respite homes in ${stateName}. Curated by Providence Partners advisors — no spam, no lead-mills.`;
    meta.setAttribute("content", desc.substring(0, 160));
  }, [stateCode, stateName, seo]);

  useEffect(() => {
    if (!stateCode) {
      setLoading(false);
      return;
    }
    let live = true;
    (async () => {
      try {
        const { data } = await api.get(`/facilities?state=${stateCode}`);
        if (live) setFacilities(data.items || []);
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [stateCode]);

  if (!stateCode) {
    return (
      <div data-testid="state-not-found" className="mx-auto max-w-2xl px-6 py-24 text-center">
        <h1 className="font-serif text-4xl text-forest">State not found</h1>
        <p className="mt-3 text-muted-foreground">"{slug}" isn't one we recognize. Try searching by city or ZIP instead.</p>
        <Button asChild className="mt-6 rounded-full bg-primary text-primary-foreground">
          <Link to="/search">Go to search</Link>
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="state-landing-page" className="bg-background">
      {/* HERO */}
      <section className="border-b border-border/60 bg-gradient-to-br from-card to-background">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 py-14 lg:grid-cols-2 lg:px-8">
          <div>
            <p className="text-sm uppercase tracking-[0.18em] text-primary">
              <MapPin className="mb-0.5 mr-1 inline h-3.5 w-3.5" /> {stateName}
            </p>
            <h1 className="mt-3 font-serif text-5xl text-forest leading-[1.05] sm:text-6xl">
              Adult Day Care, Memory Care, Respite &amp; Hospice in {stateName}
            </h1>
            <p className="mt-5 max-w-xl text-base text-muted-foreground">
              Browse member homes in <strong className="text-forest">{stateName}</strong> offering drop-in care, weekly respite, trial stays before placement,
              dedicated memory care, and <strong className="text-forest">daily-rate hospice respite in a home setting</strong>. Every listing is curated by a Providence Partners advisor — no spam, no lead-mills.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Button asChild className="rounded-full bg-primary px-6 py-6 text-base text-primary-foreground" data-testid="state-browse-btn">
                <Link to={`/search?state=${stateCode}`}>
                  Browse {stateName} homes <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <a href="tel:+15863068449">
                <Button variant="outline" className="rounded-full px-6 py-6 text-base" data-testid="state-call-btn">
                  <Phone className="mr-2 h-4 w-4" /> (586) 306-8449
                </Button>
              </a>
            </div>
            <p className="mt-4 text-sm text-muted-foreground">
              {loading
                ? "Loading member homes…"
                : facilities.length === 0
                  ? `Our member network in ${stateName} is still growing. Call us — we can still help with placement.`
                  : `${facilities.length} member home${facilities.length === 1 ? "" : "s"} in ${stateName}.`}
            </p>
          </div>
          <div className="relative">
            <img src={HERO_IMG} alt={`${stateName} senior care`} className="aspect-[4/3] w-full rounded-2xl object-cover shadow-md" />
          </div>
        </div>
      </section>

      {/* SEO CONTENT BLOCK — only renders for states with SEO content */}
      {seo && (
        <section className="border-b border-border/60 bg-card/40">
          <div className="mx-auto max-w-5xl px-6 py-12 lg:px-8">
            <h2 className="font-serif text-3xl text-forest">
              About adult day care &amp; memory care in {stateName}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-foreground">{seo.intro}</p>
            <div className="mt-6 grid gap-6 sm:grid-cols-2">
              <div>
                <h3 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Major {stateName} cities we cover</h3>
                <p className="mt-2 text-sm text-foreground" data-testid="state-cities">
                  {seo.cities.join(" · ")}
                </p>
              </div>
              <div>
                <h3 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Licensure note</h3>
                <p className="mt-2 text-sm text-foreground">{seo.note}</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FACILITY GRID */}
      {!loading && facilities.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 py-12 lg:px-8">
          <h2 className="font-serif text-3xl text-forest">Member homes in {stateName}</h2>
          <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {facilities.map((f) => (
              <Card key={f.id} data-testid={`state-facility-${f.id}`} className="overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm transition-transform hover:-translate-y-0.5">
                {f.photos && f.photos[0] && (
                  <img src={fileUrl(f.photos[0])} alt={f.name} className="aspect-[4/3] w-full object-cover" />
                )}
                <div className="p-5">
                  <Badge variant="outline" className="rounded-full text-xs">{facilityTypeLabel(f.facility_type)}</Badge>
                  <h3 className="mt-2 font-serif text-xl text-forest">{f.name}</h3>
                  <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
                    <MapPin className="h-3.5 w-3.5" /> {f.city}, {f.state}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {(f.specialties || []).slice(0, 3).map((s) => (
                      <Badge key={s} className="rounded-full bg-emerald/10 text-xs text-emerald">{specialtyLabel(s)}</Badge>
                    ))}
                  </div>
                  <Button asChild size="sm" className="mt-4 w-full rounded-full bg-primary text-primary-foreground">
                    <Link to={`/facilities/${f.id}`} data-testid={`state-facility-view-${f.id}`}>View &amp; inquire</Link>
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* CTA WHEN EMPTY */}
      {!loading && facilities.length === 0 && (
        <section className="mx-auto max-w-3xl px-6 py-16 lg:px-8">
          <Card className="rounded-2xl border border-dashed border-border/60 p-8 text-center">
            <Building2 className="mx-auto h-10 w-10 text-muted-foreground" />
            <h3 className="mt-4 font-serif text-2xl text-forest">{stateName} member network is growing</h3>
            <p className="mt-2 text-muted-foreground">
              We don't have public listings in {stateName} yet — but we can still help you find adult day care, memory care, or respite.
              Call us and a real advisor will help personally.
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <a href="tel:+15863068449">
                <Button className="rounded-full bg-primary px-6 py-6 text-base text-primary-foreground" data-testid="state-empty-call-btn">
                  <Phone className="mr-2 h-4 w-4" /> Call (586) 306-8449
                </Button>
              </a>
              <Button asChild variant="outline" className="rounded-full px-6 py-6 text-base">
                <Link to="/placement-help" data-testid="state-empty-placement-btn">Request placement help</Link>
              </Button>
            </div>
          </Card>
        </section>
      )}

      {/* OTHER STATES STRIP */}
      <section className="border-t border-border/60 bg-muted/30">
        <div className="mx-auto max-w-7xl px-6 py-10 lg:px-8">
          <div className="flex items-center justify-between gap-4">
            <h3 className="font-serif text-lg text-forest">Browse another state</h3>
            <Link to="/states" className="text-sm font-medium text-primary hover:underline" data-testid="all-states-link">
              See all 50 states <ArrowRight className="ml-1 inline h-3 w-3" />
            </Link>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {US_STATES.filter((s) => s.code !== stateCode).slice(0, 18).map((s) => (
              <Link key={s.code} to={`/state/${stateSlug(s.name)}`} data-testid={`state-chip-${s.code}`}
                className="rounded-full border border-border/60 bg-card px-3 py-1.5 text-xs text-foreground transition hover:border-primary hover:text-primary">
                {s.name}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
